import { Injectable } from '@angular/core';
import { CatalogActions } from '@catalog/data-access';
import { CityService } from '@city/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { CustomProduct } from '@product/const';
import {
  Breadcrumb,
  PaginationModel,
  ProductActions,
  ProductScope,
  ProductService,
  RoutingService,
} from '@spartacus/core';
import { Observable } from 'rxjs';
import { debounceTime, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { CatalogAnalyticsService, ProductListPageAnalyticsService, RetailRocketService } from '../services';

@Injectable()
export class CatalogAnalyticsEffects {
  onViewProductList$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.SEARCH_PRODUCTS_SUCCESS),
        switchMap((action) =>
          this.routingService.isNavigating().pipe(
            filter((isNavigating) => !isNavigating),
            take(1),
            map(() => action),
          ),
        ),
        map(({ payload: { products }, auxiliary }: ProductActions.SearchProductsSuccess) => ({ products, auxiliary })),
        filter(({ products, auxiliary }) => Boolean(products?.length) && !auxiliary),
        tap(({ products }) => {
          this.sendProductsAnalytics(products as CustomProduct[]);
        }),
        map(() => undefined),
      ),
    { dispatch: false },
  );

  onLoadMoreProducts$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogActions.LOAD_MORE_PRODUCTS_SUCCESS),
        map(({ payload: { products, pagination } }: CatalogActions.LoadMoreProductsSuccess) => ({
          products,
          pagination,
        })),
        debounceTime(500),
        tap(({ products, pagination }: { products: CustomProduct[]; pagination: PaginationModel }) => {
          this.sendProductsAnalytics(products, pagination.currentPage * pagination.pageSize);
        }),
        map(() => undefined),
      ),
    { dispatch: false },
  );

  onViewCategory$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.SEARCH_PRODUCTS_SUCCESS),
        map(({ payload: { breadcrumbs }, auxiliary }: ProductActions.SearchProductsSuccess) => ({
          breadcrumbs: breadcrumbs || [],
          auxiliary,
        })),
        filter(({ auxiliary }) => !auxiliary),
        tap(({ breadcrumbs }) => {
          this.sendBreadcrumbsAnalytics(breadcrumbs);
        }),
        map(() => undefined),
      ),
    { dispatch: false },
  );

  goToProductPage$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        filter(
          ({
            payload: {
              routerState: { semanticRoute },
            },
          }) => semanticRoute === 'product',
        ),
        map(
          ({
            payload: {
              routerState: {
                params: { productCode },
              },
            },
          }) => productCode,
        ),
        switchMap((productCode: string) =>
          this.productService.get(productCode, ProductScope.DETAILS).pipe(filter(Boolean), take(1)),
        ),
        withLatestFrom(this.cityService.activeBaseStoreCode$),
        tap(([product, activeBaseStoreCode]: [CustomProduct, string]) => {
          this.catalogAnalyticsService.onViewProductPage(product);
          this.retailRocketService.onViewProductPage({ product, activeBaseStoreCode });
        }),
        map(() => undefined),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private productListPageAnalyticsService: ProductListPageAnalyticsService,
    private productService: ProductService,
    private catalogAnalyticsService: CatalogAnalyticsService,
    private retailRocketService: RetailRocketService,
    private cityService: CityService,
    private routingService: RoutingService,
  ) {}

  private sendProductsAnalytics(products: CustomProduct[], offset?: number): void {
    if (products?.length) {
      this.productListPageAnalyticsService.onViewProductList(products, offset);
    }
  }

  private sendBreadcrumbsAnalytics(breadcrumbs: Breadcrumb[]): void {
    const breadcrumb = breadcrumbs?.[breadcrumbs.length - 1] || {};
    const categoryId =
      breadcrumb.facetCode === 'allCategories' ? Number((breadcrumb.facetValueCode || '').split('-').pop()) : 0;
    if (categoryId && !Number.isNaN(categoryId)) {
      this.retailRocketService.onViewCategoryPage({ categoryId });
    }
  }
}
